import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiEdit2, FiInfo, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { useOnboardingFlow } from "../OnboardingFlowContext";
import { getOnboardingMessages } from "../onboardingI18n";
import {
  buildRoomRateRowsPayload,
  createRoomRateRow,
  emptyWeekdayValues,
  normalizeIncomingRoomRateRows,
  normalizeWeekdayPayload,
} from "../roomRatesDraftShared";
import { rowsHaveRequiredRoomNumbers } from "../roomsDraftShared";
import { getRoomRatesMessages } from "../roomRatesMessages";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../../currency/currencyPrecision";
import "../OnboardingApp.css";

export function getRoomRateCurrencyPrecisionError(row, currencies = []) {
  const currency = String(row?.currency || "").trim().toUpperCase();
  const priceFields = [row?.min_price_amount, row?.max_price_amount];
  if (
    priceFields.some(
      (value) => value !== "" && value !== null && value !== undefined &&
        !hasValidCurrencyPrecision(value, currency, currencies)
    )
  ) {
    return { field: "price", currency, minorUnits: getCurrencyMinorUnits(currency, currencies) };
  }
  if (
    row?.penalty_type === "fixed" &&
    row?.penalty_amount !== "" &&
    row?.penalty_amount !== null &&
    row?.penalty_amount !== undefined &&
    !hasValidCurrencyPrecision(row.penalty_amount, currency, currencies)
  ) {
    return { field: "penalty", currency, minorUnits: getCurrencyMinorUnits(currency, currencies) };
  }
  return null;
}

function constrainRateRowForCategory(row, categoryMap, nextCategoryId) {
  const resolvedCategoryId = Number(nextCategoryId) || 0;
  const nextCategory = categoryMap.get(resolvedCategoryId);
  const adultsValue = row.adults === "" ? "" : String(row.adults);
  const childrenValue = row.children === "" ? "" : String(row.children);
  const maxOccupancyValue = row.max_occupancy === "" ? "" : String(row.max_occupancy);
  const maxOccupancyLimit =
    nextCategory && nextCategory.max_occupancy !== null && typeof nextCategory.max_occupancy !== "undefined"
      ? Number(nextCategory.max_occupancy)
      : null;

  return {
    ...row,
    onboarding_room_category_id: resolvedCategoryId,
    adults:
      nextCategory && adultsValue !== "" && Number(adultsValue) > Number(nextCategory.max_adults || 0)
        ? ""
        : adultsValue,
    children:
      nextCategory && childrenValue !== "" && Number(childrenValue) > Number(nextCategory.max_children || 0)
        ? ""
        : childrenValue,
    max_occupancy:
      maxOccupancyLimit !== null && maxOccupancyValue !== "" && Number(maxOccupancyValue) > maxOccupancyLimit
        ? ""
        : maxOccupancyValue,
  };
}

function getRateValidationError({
  row,
  copy,
  categoryMap,
  weekdayLabels,
  allowedCurrencyCodes,
  currencies,
  mode = "grid",
  index = 1,
}) {
  const category = categoryMap.get(Number(row.onboarding_room_category_id));
  const rateCode = String(row.rate_code || "").trim().toUpperCase();
  const name = String(row.name || "").trim();
  const currency = String(row.currency || "").trim().toUpperCase();
  const usesWeekdayStay =
    row.use_stay_defaults_by_weekday === true || row.use_stay_defaults_by_weekday === "true";
  const weekdayMinResult = normalizeWeekdayPayload(row.default_min_stay_by_weekday);
  const weekdayMaxResult = normalizeWeekdayPayload(row.default_max_stay_by_weekday);
  const minPrice = row.min_price_amount === "" ? null : Number(row.min_price_amount);
  const maxPrice = row.max_price_amount === "" ? null : Number(row.max_price_amount);
  const penaltyAmount = row.penalty_amount === "" ? null : Number(row.penalty_amount);
  const defaultMinStay = row.default_min_stay === "" ? null : Number(row.default_min_stay);
  const defaultMaxStay = row.default_max_stay === "" ? null : Number(row.default_max_stay);
  const prefix = mode === "create" ? "create" : "validation";
  const pick = (key, ...args) => {
    const value = prefix === "create" ? copy[`create${key}`] : copy[`validation${key}`];
    if (typeof value !== "function") return value;
    return prefix === "create" ? value(...args) : value(index, ...args);
  };

  if (!rateCode) return pick("RateCodeRequired");
  if (rateCode.length > 6) return pick("RateCodeLength");
  if (!name) return pick("NameRequired");
  if (!category) return pick("CategoryRequired");
  if (!currency) return pick("CurrencyRequired");
  if (allowedCurrencyCodes.size > 0 && !allowedCurrencyCodes.has(currency)) return pick("CurrencyInvalid");

  if (row.adults !== "") {
    const adultsValue = Number(row.adults);
    if (!Number.isInteger(adultsValue) || adultsValue < 1) return pick("AdultsInvalid");
    if (adultsValue > Number(category.max_adults || 0)) return pick("AdultsCap", Number(category.max_adults || 0));
  }

  if (row.children !== "") {
    const childrenValue = Number(row.children);
    if (!Number.isInteger(childrenValue) || childrenValue < 0) return pick("ChildrenInvalid");
    if (childrenValue > Number(category.max_children || 0))
      return pick("ChildrenCap", Number(category.max_children || 0));
  }

  if (row.max_occupancy !== "") {
    const maxOccupancyValue = Number(row.max_occupancy);
    const categoryCap =
      category.max_occupancy !== null && typeof category.max_occupancy !== "undefined"
        ? Number(category.max_occupancy)
        : null;
    if (!Number.isInteger(maxOccupancyValue) || maxOccupancyValue < 1) return pick("MaxOccupancyInvalid");
    if (categoryCap !== null && maxOccupancyValue > categoryCap) return pick("MaxOccupancyCap", categoryCap);
  }

  if (
    (minPrice !== null && (!Number.isFinite(minPrice) || minPrice < 0)) ||
    (maxPrice !== null && (!Number.isFinite(maxPrice) || maxPrice < 0)) ||
    (minPrice !== null && maxPrice !== null && minPrice > maxPrice)
  ) {
    return pick("Price");
  }

  const precisionError = getRoomRateCurrencyPrecisionError(row, currencies);
  if (precisionError?.field === "price") {
    return pick("PricePrecision", precisionError.currency, precisionError.minorUnits);
  }

  if (penaltyAmount !== null && (!Number.isFinite(penaltyAmount) || penaltyAmount < 0)) return pick("PenaltyAmount");
  if (precisionError?.field === "penalty") {
    return pick("PenaltyPrecision", precisionError.currency, precisionError.minorUnits);
  }

  if (!usesWeekdayStay) {
    if (defaultMinStay !== null && (!Number.isInteger(defaultMinStay) || defaultMinStay < 1)) return pick("MinStay");
    if (defaultMaxStay !== null && (!Number.isInteger(defaultMaxStay) || defaultMaxStay < 1)) return pick("MaxStay");
    if (defaultMinStay !== null && defaultMaxStay !== null && defaultMinStay > defaultMaxStay) return pick("StayRange");
    return null;
  }

  if (weekdayMinResult.errorKey) return pick("WeekdayMin", weekdayLabels.get(weekdayMinResult.errorKey) || weekdayMinResult.errorKey);
  if (weekdayMaxResult.errorKey) return pick("WeekdayMax", weekdayLabels.get(weekdayMaxResult.errorKey) || weekdayMaxResult.errorKey);
  if (!weekdayMinResult.value && !weekdayMaxResult.value) return pick("WeekdayModeEmpty");

  const weekdayMinMap = weekdayMinResult.value || {};
  const weekdayMaxMap = weekdayMaxResult.value || {};
  for (const [dayKey, dayLabel] of weekdayLabels.entries()) {
    const minValue = weekdayMinMap[dayKey];
    const maxValue = weekdayMaxMap[dayKey];
    if (
      minValue !== null &&
      typeof minValue !== "undefined" &&
      maxValue !== null &&
      typeof maxValue !== "undefined" &&
      minValue > maxValue
    ) {
      return pick("WeekdayRange", dayLabel);
    }
  }

  return null;
}

function FieldHelp({ text, ariaLabel, placement = "top-center" }) {
  return (
    <span className={`onboarding-helpWrap ${placement !== "top-center" ? `onboarding-helpWrap--${placement}` : ""}`}>
      <button type="button" className="onboarding-helpButton" aria-label={ariaLabel}>
        <FiInfo size={14} />
      </button>
      <span className="onboarding-helpTooltip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function getWeekdayStayRangeLabel(source, emptyLabel) {
  const normalized = normalizeWeekdayPayload(source).value;
  if (!normalized) return emptyLabel;

  const values = Object.values(normalized).filter((value) => Number.isInteger(value));
  if (!values.length) return emptyLabel;

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  return minValue === maxValue ? String(minValue) : `${minValue}-${maxValue}`;
}

function sanitizeRateRowForBookingEngine(row, bookingEngineEnabled) {
  if (bookingEngineEnabled || !row) {
    return row;
  }

  return {
    ...row,
    show_in_booking_engine: false,
  };
}

function sanitizeRateRowsForBookingEngine(rows, bookingEngineEnabled) {
  return rows.map((row) => sanitizeRateRowForBookingEngine(row, bookingEngineEnabled));
}

export default function RoomRatesStep({ bookingEngineEnabled = false }) {
  const navigate = useNavigate();
  const { languageId } = usePmsTheme();
  const { clearStepCommandState, markStepComplete, markStepIncomplete, setStepCommandState, steps } = useOnboardingFlow();
  const onboardingCopy = getOnboardingMessages(languageId);
  const copy = getRoomRatesMessages(languageId).grid;
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [rows, setRows] = useState([]);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [editorRow, setEditorRow] = useState(null);
  const [editorMode, setEditorMode] = useState("create");
  const [editorFeedback, setEditorFeedback] = useState("");
  const editorCodeRef = useRef(null);
  const editorModalRef = useRef(null);
  const pendingEditorScrollRef = useRef(null);
  const stepNumber = useMemo(() => Math.max(steps.findIndex((step) => step.id === "room-rates") + 1, 1), [steps]);
  const categoryMap = useMemo(() => new Map(categories.map((category) => [Number(category.id), category])), [categories]);
  const clientMap = useMemo(() => new Map(clients.map((client) => [Number(client.id), client])), [clients]);
  const editorRowKey = editorRow?.row_key || null;
  const allowedCurrencyCodes = useMemo(
    () => new Set(currencies.map((currency) => String(currency.code || "").trim().toUpperCase()).filter(Boolean)),
    [currencies]
  );
  const weekdayLabels = useMemo(() => new Map(copy.weekdays.map((day) => [day.key, day.label])), [copy.weekdays]);
  const usedCategoryCount = useMemo(() => new Set(rows.map((row) => Number(row.onboarding_room_category_id)).filter(Boolean)).size, [rows]);
  const formattedSavedAt = useMemo(() => {
    if (!sessionMeta?.updated_at) return copy.summaryNotSaved;
    const parsedDate = new Date(sessionMeta.updated_at);
    if (Number.isNaN(parsedDate.getTime())) return copy.summaryNotSaved;
    return new Intl.DateTimeFormat(languageId || "en", { dateStyle: "short", timeStyle: "short" }).format(parsedDate);
  }, [copy.summaryNotSaved, languageId, sessionMeta?.updated_at]);

  const closeEditor = useCallback(() => {
    setEditorRow(null);
    setEditorFeedback("");
  }, []);

  const openCreateModal = useCallback(() => {
    setEditorMode("create");
    setEditorRow(sanitizeRateRowForBookingEngine(createRoomRateRow(), bookingEngineEnabled));
    setEditorFeedback("");
    setFeedback({ type: "", message: "" });
  }, [bookingEngineEnabled]);

  const openEditModal = useCallback((row) => {
    setEditorMode("edit");
    setEditorRow(sanitizeRateRowForBookingEngine(createRoomRateRow(row), bookingEngineEnabled));
    setEditorFeedback("");
    setFeedback({ type: "", message: "" });
  }, [bookingEngineEnabled]);

  const updateEditorRow = useCallback((updater) => {
    setEditorRow((currentRow) => {
      if (!currentRow) return currentRow;
      const nextRow = typeof updater === "function" ? updater(currentRow) : { ...currentRow, ...updater };
      return sanitizeRateRowForBookingEngine(nextRow, bookingEngineEnabled);
    });
  }, [bookingEngineEnabled]);

  const handleEditorCategoryChange = (value) => {
    setEditorFeedback("");
    updateEditorRow((row) => constrainRateRowForCategory(row, categoryMap, value));
  };

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setFeedback({ type: "", message: "" });
    try {
      const [roomRatesResponse, roomsResponse] = await Promise.all([axios.get("/api/onboarding/room-rates"), axios.get("/api/onboarding/rooms")]);
      const prerequisiteRows = Array.isArray(roomsResponse.data?.rows) ? roomsResponse.data.rows : [];
      if (!rowsHaveRequiredRoomNumbers(prerequisiteRows)) {
        navigate("/onboarding/rooms-grid", { replace: true });
        return;
      }
      const incomingRows = sanitizeRateRowsForBookingEngine(
        normalizeIncomingRoomRateRows(roomRatesResponse.data?.rows || []),
        bookingEngineEnabled
      );
      setCategories(Array.isArray(roomRatesResponse.data?.categories) ? roomRatesResponse.data.categories : []);
      setClients(Array.isArray(roomRatesResponse.data?.clients) ? roomRatesResponse.data.clients : []);
      setCurrencies(Array.isArray(roomRatesResponse.data?.currencies) ? roomRatesResponse.data.currencies : []);
      setRows(incomingRows);
      setSessionMeta(roomRatesResponse.data?.session || null);
      setIsDirty(false);
      setLoadFailed(false);
      if (incomingRows.length > 0) markStepComplete("room-rates");
      else markStepIncomplete("room-rates");
    } catch (error) {
      console.error("[Onboarding][RoomRates] Error loading draft:", error);
      setCategories([]);
      setClients([]);
      setCurrencies([]);
      setRows([]);
      setSessionMeta(null);
      setLoadFailed(true);
      markStepIncomplete("room-rates");
      setFeedback({ type: "error", message: copy.loadError });
    } finally {
      setLoading(false);
    }
  }, [bookingEngineEnabled, copy.loadError, markStepComplete, markStepIncomplete, navigate]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  useEffect(() => {
    if (!editorRow) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeEditor();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeEditor, editorRow]);

  useEffect(() => {
    if (editorRowKey && editorCodeRef.current) editorCodeRef.current.focus();
  }, [editorRowKey]);

  useEffect(() => {
    if (!pendingEditorScrollRef.current) return;
    const { modalScrollTop, windowScrollY } = pendingEditorScrollRef.current;
    pendingEditorScrollRef.current = null;
    window.requestAnimationFrame(() => {
      if (editorModalRef.current) {
        editorModalRef.current.scrollTop = modalScrollTop;
      }
      if (typeof windowScrollY === "number") {
        window.scrollTo({ top: windowScrollY, behavior: "auto" });
      }
    });
  }, [editorRow?.use_stay_defaults_by_weekday]);

  const validateBeforeSave = useCallback(() => {
    for (const [index, row] of rows.entries()) {
      const validationError = getRateValidationError({
        row,
        copy,
        categoryMap,
        weekdayLabels,
        allowedCurrencyCodes,
        currencies,
        mode: "grid",
        index: index + 1,
      });
      if (validationError) return validationError;
    }
    return null;
  }, [allowedCurrencyCodes, categoryMap, copy, currencies, rows, weekdayLabels]);

  const fillWeekdayValuesFromDefault = (fieldKey, defaultValue) => {
    const fillValue = defaultValue === "" ? "" : String(defaultValue);
    setEditorFeedback("");
    updateEditorRow({
      [fieldKey]: copy.weekdays.reduce((accumulator, day) => {
        accumulator[day.key] = fillValue;
        return accumulator;
      }, {}),
    });
  };

  const clearWeekdayValues = () => {
    setEditorFeedback("");
    updateEditorRow({
      default_min_stay_by_weekday: emptyWeekdayValues(),
      default_max_stay_by_weekday: emptyWeekdayValues(),
    });
  };

  const setStayMode = (usesWeekdayStay) => {
    if (!editorRow || editorRow.use_stay_defaults_by_weekday === usesWeekdayStay) {
      return;
    }
    pendingEditorScrollRef.current = {
      modalScrollTop: editorModalRef.current?.scrollTop ?? 0,
      windowScrollY: typeof window !== "undefined" ? window.scrollY : 0,
    };
    setEditorFeedback("");
    updateEditorRow({ use_stay_defaults_by_weekday: usesWeekdayStay });
  };

  const removeRow = (rowKey) => {
    setRows((currentRows) => currentRows.filter((row) => row.row_key !== rowKey));
    setIsDirty(true);
    setFeedback({ type: "", message: "" });
  };

  const confirmEditor = useCallback(() => {
    if (!editorRow) return;
    const validationError = getRateValidationError({
      row: editorRow,
      copy,
      categoryMap,
      weekdayLabels,
      allowedCurrencyCodes,
      currencies,
      mode: "create",
    });
    if (validationError) {
      setEditorFeedback(validationError);
      return;
    }
    const nextRow = sanitizeRateRowForBookingEngine(createRoomRateRow(editorRow), bookingEngineEnabled);
    setRows((currentRows) =>
      editorMode === "edit"
        ? currentRows.map((row) => (row.row_key === nextRow.row_key ? nextRow : row))
        : [...currentRows, nextRow]
    );
    setIsDirty(true);
    setEditorFeedback("");
    setEditorRow(null);
    setFeedback({ type: "success", message: editorMode === "edit" ? copy.editSuccess : copy.addSuccess });
  }, [allowedCurrencyCodes, bookingEngineEnabled, categoryMap, copy, currencies, editorMode, editorRow, weekdayLabels]);

  const handleSave = useCallback(async () => {
    const validationError = validateBeforeSave();
    if (validationError) {
      setFeedback({ type: "error", message: validationError });
      return false;
    }
    setSaving(true);
    setFeedback({ type: "", message: "" });
    try {
      const response = await axios.put("/api/onboarding/room-rates", {
        rows: buildRoomRateRowsPayload(rows, { bookingEngineEnabled }),
      });
      const savedRows = sanitizeRateRowsForBookingEngine(
        normalizeIncomingRoomRateRows(response.data?.rows || []),
        bookingEngineEnabled
      );
      setRows(savedRows);
      setCategories(Array.isArray(response.data?.categories) ? response.data.categories : []);
      setClients(Array.isArray(response.data?.clients) ? response.data.clients : []);
      setCurrencies(Array.isArray(response.data?.currencies) ? response.data.currencies : []);
      setSessionMeta(response.data?.session || null);
      setIsDirty(false);
      if (savedRows.length > 0) markStepComplete("room-rates");
      else markStepIncomplete("room-rates");
      setFeedback({ type: "success", message: copy.saveSuccess });
      return true;
    } catch (error) {
      console.error("[Onboarding][RoomRates] Error saving draft:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.saveError });
      return false;
    } finally {
      setSaving(false);
    }
  }, [bookingEngineEnabled, copy.saveError, copy.saveSuccess, markStepComplete, markStepIncomplete, rows, validateBeforeSave]);

  useEffect(() => {
    setStepCommandState("room-rates", {
      onSave: handleSave,
      canSave: !loading && !editorRow,
      canProceed: !isDirty && !editorRow,
      isDirty,
      isSaving: saving,
    });
    return () => clearStepCommandState("room-rates");
  }, [clearStepCommandState, editorRow, handleSave, isDirty, loading, saving, setStepCommandState]);

  const getMatchLabel = (matchType) => (matchType === "exact" ? copy.matchExact : copy.matchUpto);
  const getPenaltyLabel = (penaltyType) => {
    if (penaltyType === "percent") return copy.penaltyTypePercent;
    if (penaltyType === "fixed") return copy.penaltyTypeFixed;
    return copy.penaltyTypeNone;
  };
  const selectedEditorCategory = editorRow ? categoryMap.get(Number(editorRow.onboarding_room_category_id)) : null;
  const adultOptions = Array.from({ length: Number(selectedEditorCategory?.max_adults || 0) }, (_value, index) => index + 1);
  const childrenOptions = Array.from(
    { length: Number(selectedEditorCategory?.max_children || 0) + 1 },
    (_value, index) => index
  );
  const maxOccupancyLimit =
    selectedEditorCategory &&
    selectedEditorCategory.max_occupancy !== null &&
    typeof selectedEditorCategory.max_occupancy !== "undefined"
      ? Number(selectedEditorCategory.max_occupancy)
      : null;
  const maxOccupancyOptions =
    maxOccupancyLimit && maxOccupancyLimit > 0
      ? Array.from({ length: maxOccupancyLimit }, (_value, index) => index + 1)
      : [];

  if (loading) {
    return (
      <div className="onboarding-emptyState">
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{copy.copy}</p>
      </div>
    );
  }

  if (!categories.length) {
    return (
      <div className="onboarding-emptyState onboarding-formCard--centered">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{loadFailed ? copy.title : copy.noCategoriesTitle}</h3>
        <p className="onboarding-sectionText">{loadFailed ? feedback.message || copy.loadError : copy.noCategoriesCopy}</p>
      </div>
    );
  }

  return (
    <>
      <div className="onboarding-grid">
        <section className="onboarding-formCard">
          <div className="onboarding-formHeader">
            <div>
              <div className="onboarding-eyebrow">{copy.eyebrow}</div>
              <div className="onboarding-stepCount">{onboardingCopy.progress.stepCounter(stepNumber, steps.length)}</div>
              <h3 className="onboarding-sectionTitle">{copy.title}</h3>
              <p className="onboarding-sectionText">{copy.copy}</p>
            </div>
            <div className="onboarding-formActions">
              <button type="button" className="onboarding-secondaryBtn" onClick={openCreateModal} disabled={saving}>
                <FiPlus size={16} />
                {copy.add}
              </button>
            </div>
          </div>

          {rows.length ? (
            <div className="onboarding-rateTableWrap">
              <table className="onboarding-rateTable onboarding-rateTable--descriptive">
                <thead>
                  <tr>
                    <th>{copy.rateCode}</th>
                    <th>{copy.rate}</th>
                    <th>{copy.roomCategory}</th>
                    <th>{copy.occupancy}</th>
                    <th>{copy.stayRules}</th>
                    <th>{copy.policy}</th>
                    <th>{copy.visibility}</th>
                    <th>{copy.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const category = categoryMap.get(Number(row.onboarding_room_category_id));
                    const client = clientMap.get(Number(row.client_id));
                    const minWeekdayRange = getWeekdayStayRangeLabel(row.default_min_stay_by_weekday, copy.summaryNone);
                    const maxWeekdayRange = getWeekdayStayRangeLabel(row.default_max_stay_by_weekday, copy.summaryNone);
                    const bookingLabel = row.show_in_booking_engine ? copy.bookingEngineVisible : copy.bookingEngineHidden;
                    const usesWeekdayStay =
                      row.use_stay_defaults_by_weekday === true || row.use_stay_defaults_by_weekday === "true";
                    return (
                      <tr key={row.row_key}>
                        <td>
                          <div className="onboarding-rateSummary">
                            <strong>{row.rate_code || copy.rateCodePlaceholder}</strong>
                            <small>{row.currency || copy.summaryNoCurrency}</small>
                          </div>
                        </td>
                        <td>
                          <button type="button" className="onboarding-rateSummaryButton" onClick={() => openEditModal(row)}>
                            <span className="onboarding-rateSummary">
                              <strong>{row.name || copy.untitledRate}</strong>
                              <small>{client?.name || copy.summaryNoClient}</small>
                            </span>
                          </button>
                        </td>
                        <td>
                          <div className="onboarding-rateSummary">
                            <strong>{category?.name || copy.summaryNoCategory}</strong>
                          </div>
                        </td>
                        <td>
                          <div className="onboarding-rateSummary">
                            <small>{copy.summaryAdultsLine(row.adults, getMatchLabel(row.adults_match_type))}</small>
                            <small>{copy.summaryChildrenLine(row.children, getMatchLabel(row.children_match_type))}</small>
                            <small>{copy.summaryMaxOccupancyLine(row.max_occupancy, getMatchLabel(row.max_occupancy_match_type))}</small>
                          </div>
                        </td>
                        <td>
                          <div className="onboarding-rateSummary">
                            {usesWeekdayStay ? (
                              <>
                                <small>{copy.summaryWeekdayMinLine(minWeekdayRange)}</small>
                                <small>{copy.summaryWeekdayMaxLine(maxWeekdayRange)}</small>
                              </>
                            ) : (
                              <>
                                <small>{copy.summaryMinStayLine(row.default_min_stay)}</small>
                                <small>{copy.summaryMaxStayLine(row.default_max_stay)}</small>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="onboarding-rateSummary">
                            <small>{copy.summaryCancellationLine(Boolean(String(row.cancellation_policy || "").trim()))}</small>
                            <small>{copy.summaryPenaltyLine(getPenaltyLabel(row.penalty_type), row.penalty_amount)}</small>
                            <small>
                              {copy.refundable}: {row.is_refundable ? copy.refundableYes : copy.refundableNo}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div className="onboarding-rateSummary">
                            {bookingEngineEnabled ? <small>{copy.summaryVisibilityLine(bookingLabel)}</small> : null}
                            <small>
                              {copy.minPriceAmount}: {row.min_price_amount === "" ? copy.summaryNone : row.min_price_amount}
                            </small>
                            <small>
                              {copy.maxPriceAmount}: {row.max_price_amount === "" ? copy.summaryNone : row.max_price_amount}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div className="onboarding-rateActionGroup">
                            <button type="button" className="onboarding-tableAction" onClick={() => openEditModal(row)} aria-label={copy.edit} title={copy.edit}>
                              <FiEdit2 size={15} />
                            </button>
                            <button type="button" className="onboarding-tableAction" onClick={() => removeRow(row.row_key)} aria-label={copy.remove} title={copy.remove}>
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="onboarding-emptyState onboarding-emptyState--compact">
              <h4 className="onboarding-sectionTitle">{copy.emptyTitle}</h4>
              <p className="onboarding-sectionText">{copy.emptyCopy}</p>
              <div className="onboarding-toolbar onboarding-toolbar--centered">
                <button type="button" className="onboarding-primaryBtn" onClick={openCreateModal}>
                  <FiPlus size={16} />
                  {copy.add}
                </button>
              </div>
            </div>
          )}

          {feedback.message ? (
            <div className={`onboarding-status ${feedback.type === "success" ? "onboarding-status--success" : "onboarding-status--error"}`}>
              {feedback.message}
            </div>
          ) : null}
        </section>

        <aside className="onboarding-infoCard">
          <div className="onboarding-eyebrow">{copy.eyebrow}</div>
          <h3 className="onboarding-sectionTitle">{copy.summaryTitle}</h3>
          <p className="onboarding-sectionText">{copy.summaryIntro}</p>
          <div className="onboarding-pointsList">
            {copy.summaryPoints.map((point) => (
              <div key={point} className="onboarding-pointItem">
                <span className="onboarding-pointDot" />
                <span>{point}</span>
              </div>
            ))}
          </div>
          <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryRatesLabel}</span><span className="onboarding-kpiValue">{rows.length}</span></div>
          <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryCategoriesLabel}</span><span className="onboarding-kpiValue">{usedCategoryCount}</span></div>
          <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summarySavedLabel}</span><span className="onboarding-kpiValue">{formattedSavedAt}</span></div>
        </aside>
      </div>

      {editorRow ? (
        <div className="onboarding-entryModal__backdrop" onClick={closeEditor}>
          <div className="onboarding-entryModal onboarding-entryModal--wide" ref={editorModalRef} onClick={(event) => event.stopPropagation()}>
            <div className="onboarding-entryModal__header">
              <div>
                <h4>{editorMode === "edit" ? copy.editModalTitle(editorRow.name || editorRow.rate_code) : copy.createModalTitle}</h4>
                <p className="onboarding-entryModal__copy">{editorMode === "edit" ? copy.editModalCopy : copy.createModalCopy}</p>
              </div>
              <button type="button" className="onboarding-ratePopoverClose" onClick={closeEditor} aria-label={copy.createModalCancel} title={copy.createModalCancel}>
                <FiX size={16} />
              </button>
            </div>

            <div className="onboarding-entryModal__body">
              {copy.createModalHint ? <div className="onboarding-entryModal__hint">{copy.createModalHint}</div> : null}

              <section className="onboarding-rateEditorSection">
                <div className="onboarding-rateEditorSectionHead"><h5>{copy.sectionBasics}</h5><p>{copy.sectionBasicsCopy}</p></div>
                <div className="onboarding-fields">
                  <label className="onboarding-field"><span className="onboarding-label">{copy.rateCode}</span><input ref={editorCodeRef} className="onboarding-input" maxLength={6} value={editorRow.rate_code} placeholder={copy.rateCodePlaceholder} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ rate_code: event.target.value.toUpperCase() }); }} /></label>
                  <label className="onboarding-field"><span className="onboarding-label">{copy.name}</span><input className="onboarding-input" value={editorRow.name} placeholder={copy.namePlaceholder} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ name: event.target.value }); }} /></label>
                </div>
                <div className="onboarding-fields onboarding-fields--threeCols">
                  <label className="onboarding-field"><span className="onboarding-label">{copy.roomCategory}</span><select className="onboarding-input" value={editorRow.onboarding_room_category_id || ""} onChange={(event) => handleEditorCategoryChange(event.target.value)}><option value="">{copy.roomCategoryPlaceholder}</option>{categories.map((categoryOption) => <option key={categoryOption.id} value={categoryOption.id}>{categoryOption.name}</option>)}</select></label>
                  <label className="onboarding-field"><span className="onboarding-label">{copy.client}</span><select className="onboarding-input" value={editorRow.client_id === "" ? "" : String(editorRow.client_id)} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ client_id: event.target.value }); }}><option value="">{copy.clientPlaceholder}</option>{clients.map((clientOption) => <option key={clientOption.id} value={clientOption.id}>{clientOption.name}</option>)}</select></label>
                  <label className="onboarding-field">
                    <span className="onboarding-label">{copy.currency}</span>
                    <select
                      className="onboarding-input"
                      value={editorRow.currency}
                      onChange={(event) => {
                        setEditorFeedback("");
                        updateEditorRow({ currency: event.target.value.toUpperCase() });
                      }}
                    >
                      <option value="">{copy.currencyPlaceholder}</option>
                      {currencies.map((currencyOption) => (
                        <option key={currencyOption.id || currencyOption.code} value={currencyOption.code}>
                          {[currencyOption.code, currencyOption.name, currencyOption.symbol].filter(Boolean).join(" - ")}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>

              <section className="onboarding-rateEditorSection">
                <div className="onboarding-rateEditorSectionHead">
                  <h5 className="onboarding-labelWithHelp">
                    {copy.sectionVisibility}
                    <FieldHelp text={copy.sectionVisibilityHelp} ariaLabel={copy.sectionVisibilityHelpAria} placement="bottom-start" />
                  </h5>
                  <p>{copy.sectionVisibilityCopy}</p>
                </div>
                <div className="onboarding-fields">
                  <label className="onboarding-field"><span className="onboarding-label">{copy.refundable}</span><select className="onboarding-input" value={String(editorRow.is_refundable)} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ is_refundable: event.target.value === "true" }); }}><option value="true">{copy.refundableYes}</option><option value="false">{copy.refundableNo}</option></select></label>
                  {bookingEngineEnabled ? (
                    <label className="onboarding-field"><span className="onboarding-label onboarding-labelWithHelp">{copy.showInBookingEngine}<FieldHelp text={copy.bookingEngineHelp} ariaLabel={copy.bookingEngineHelpAria} /></span><select className="onboarding-input" value={String(editorRow.show_in_booking_engine)} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ show_in_booking_engine: event.target.value === "true" }); }}><option value="true">{copy.bookingEngineVisible}</option><option value="false">{copy.bookingEngineHidden}</option></select></label>
                  ) : null}
                </div>
              </section>

              <section className="onboarding-rateEditorSection">
                <div className="onboarding-rateEditorSectionHead"><h5>{copy.sectionOccupancy}</h5><p>{copy.sectionOccupancyCopy}</p></div>
                <div className="onboarding-fields onboarding-fields--threeCols">
                  <div className="onboarding-fieldGroup">
                    <span className="onboarding-label onboarding-labelWithHelp">
                      {copy.adults}
                      <FieldHelp text={copy.adultsHelp} ariaLabel={copy.adultsHelpAria} placement="bottom-start" />
                    </span>
                    <div className={`onboarding-fieldPair ${editorRow.adults === "" ? "onboarding-fieldPair--single" : ""}`}>
                      <select className="onboarding-input" value={editorRow.adults === "" ? "" : String(editorRow.adults)} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ adults: event.target.value }); }} disabled={!selectedEditorCategory}>
                        <option value="">{copy.adultsAny}</option>
                        {adultOptions.map((optionValue) => <option key={optionValue} value={optionValue}>{optionValue}</option>)}
                      </select>
                      {editorRow.adults !== "" ? (
                        <select className="onboarding-input" value={editorRow.adults_match_type} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ adults_match_type: event.target.value }); }}>
                          <option value="exact">{copy.matchExact}</option>
                          <option value="upto">{copy.matchUpto}</option>
                        </select>
                      ) : null}
                    </div>
                  </div>
                  <div className="onboarding-fieldGroup">
                    <span className="onboarding-label onboarding-labelWithHelp">
                      {copy.children}
                      <FieldHelp text={copy.childrenHelp} ariaLabel={copy.childrenHelpAria} />
                    </span>
                    <div className={`onboarding-fieldPair ${editorRow.children === "" ? "onboarding-fieldPair--single" : ""}`}>
                      <select className="onboarding-input" value={editorRow.children === "" ? "" : String(editorRow.children)} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ children: event.target.value }); }} disabled={!selectedEditorCategory}>
                        <option value="">{copy.childrenAny}</option>
                        {childrenOptions.map((optionValue) => <option key={optionValue} value={optionValue}>{optionValue}</option>)}
                      </select>
                      {editorRow.children !== "" ? (
                        <select className="onboarding-input" value={editorRow.children_match_type} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ children_match_type: event.target.value }); }}>
                          <option value="upto">{copy.matchUpto}</option>
                          <option value="exact">{copy.matchExact}</option>
                        </select>
                      ) : null}
                    </div>
                  </div>
                  <div className="onboarding-fieldGroup">
                    <span className="onboarding-label onboarding-labelWithHelp">
                      {copy.maxOccupancy}
                      <FieldHelp text={copy.maxOccupancyHelp} ariaLabel={copy.maxOccupancyHelpAria} />
                    </span>
                    <div className={`onboarding-fieldPair ${editorRow.max_occupancy === "" ? "onboarding-fieldPair--single" : ""}`}>
                      <select className="onboarding-input" value={editorRow.max_occupancy === "" ? "" : String(editorRow.max_occupancy)} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ max_occupancy: event.target.value }); }} disabled={maxOccupancyLimit === null}>
                        <option value="">{copy.maxOccupancyAny}</option>
                        {maxOccupancyOptions.map((optionValue) => <option key={optionValue} value={optionValue}>{optionValue}</option>)}
                      </select>
                      {editorRow.max_occupancy !== "" ? (
                        <select className="onboarding-input" value={editorRow.max_occupancy_match_type} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ max_occupancy_match_type: event.target.value }); }}>
                          <option value="upto">{copy.matchUpto}</option>
                          <option value="exact">{copy.matchExact}</option>
                        </select>
                      ) : null}
                    </div>
                  </div>
                </div>
              </section>

              <section className="onboarding-rateEditorSection">
                <div className="onboarding-rateEditorSectionHead"><h5>{copy.sectionPriceAndStay}</h5><p>{copy.sectionPriceAndStayCopy}</p></div>
                <div className="onboarding-fields">
                  <label className="onboarding-field"><span className="onboarding-label onboarding-labelWithHelp">{copy.minPriceAmount}<FieldHelp text={copy.minPriceHelp} ariaLabel={copy.minPriceHelpAria} placement="bottom-start" /></span><input className="onboarding-input" type="number" min="0" step={currencyAmountStep(editorRow.currency, currencies)} value={editorRow.min_price_amount} placeholder={copy.minPricePlaceholder} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ min_price_amount: event.target.value }); }} /></label>
                  <label className="onboarding-field"><span className="onboarding-label onboarding-labelWithHelp">{copy.maxPriceAmount}<FieldHelp text={copy.maxPriceHelp} ariaLabel={copy.maxPriceHelpAria} /></span><input className="onboarding-input" type="number" min="0" step={currencyAmountStep(editorRow.currency, currencies)} value={editorRow.max_price_amount} placeholder={copy.maxPricePlaceholder} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ max_price_amount: event.target.value }); }} /></label>
                  <div className="onboarding-fieldGroup onboarding-fieldGroup--full">
                    <span className="onboarding-label">{copy.stayRules}</span>
                    <div className="onboarding-segmentedControl">
                      <button type="button" className={`onboarding-segmentBtn ${editorRow.use_stay_defaults_by_weekday ? "" : "is-active"}`} onClick={() => setStayMode(false)}>
                        {copy.stayModeDefault}
                      </button>
                      <button type="button" className={`onboarding-segmentBtn ${editorRow.use_stay_defaults_by_weekday ? "is-active" : ""}`} onClick={() => setStayMode(true)}>
                        {copy.stayModeWeekday}
                      </button>
                    </div>
                    <p className="onboarding-inlineHint">{copy.stayModeHint}</p>
                  </div>
                  {editorRow.use_stay_defaults_by_weekday ? (
                    <div className="onboarding-fieldGroup onboarding-fieldGroup--full">
                      <div className="onboarding-toolbar onboarding-toolbar--compact">
                        <button type="button" className="onboarding-secondaryBtn" onClick={() => fillWeekdayValuesFromDefault("default_min_stay_by_weekday", editorRow.default_min_stay)}>{copy.weekdayFillMin}</button>
                        <button type="button" className="onboarding-secondaryBtn" onClick={() => fillWeekdayValuesFromDefault("default_max_stay_by_weekday", editorRow.default_max_stay)}>{copy.weekdayFillMax}</button>
                        <button type="button" className="onboarding-ghostBtn" onClick={clearWeekdayValues}>{copy.weekdayClear}</button>
                      </div>
                      <div className="onboarding-rateWeekdayMatrix">
                        {copy.weekdays.map((day) => (
                          <div key={day.key} className="onboarding-rateWeekdayCard">
                            <strong>{day.label}</strong>
                            <input className="onboarding-input" type="number" min="1" step="1" placeholder={copy.weekdayMinShort} value={editorRow.default_min_stay_by_weekday?.[day.key] ?? ""} onChange={(event) => { setEditorFeedback(""); updateEditorRow((row) => ({ ...row, default_min_stay_by_weekday: { ...row.default_min_stay_by_weekday, [day.key]: event.target.value } })); }} />
                            <input className="onboarding-input" type="number" min="1" step="1" placeholder={copy.weekdayMaxShort} value={editorRow.default_max_stay_by_weekday?.[day.key] ?? ""} onChange={(event) => { setEditorFeedback(""); updateEditorRow((row) => ({ ...row, default_max_stay_by_weekday: { ...row.default_max_stay_by_weekday, [day.key]: event.target.value } })); }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="onboarding-field"><span className="onboarding-label">{copy.defaultMinStay}</span><input className="onboarding-input" type="number" min="1" step="1" value={editorRow.default_min_stay} placeholder={copy.minStayPlaceholder} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ default_min_stay: event.target.value }); }} /></label>
                      <label className="onboarding-field"><span className="onboarding-label">{copy.defaultMaxStay}</span><input className="onboarding-input" type="number" min="1" step="1" value={editorRow.default_max_stay} placeholder={copy.maxStayPlaceholder} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ default_max_stay: event.target.value }); }} /></label>
                    </>
                  )}
                </div>
              </section>

              <section className="onboarding-rateEditorSection">
                <div className="onboarding-rateEditorSectionHead"><h5>{copy.sectionPolicies}</h5><p>{copy.sectionPoliciesCopy}</p></div>
                <div className="onboarding-fields onboarding-fields--policyRow">
                  <label className="onboarding-field onboarding-field--full"><span className="onboarding-label">{copy.cancellationPolicy}</span><textarea className="onboarding-textarea" value={editorRow.cancellation_policy} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ cancellation_policy: event.target.value }); }} /></label>
                  <label className="onboarding-field"><span className="onboarding-label">{copy.penaltyType}</span><select className="onboarding-input" value={editorRow.penalty_type} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ penalty_type: event.target.value }); }}><option value="">{copy.penaltyTypeNone}</option><option value="fixed">{copy.penaltyTypeFixed}</option><option value="percent">{copy.penaltyTypePercent}</option></select></label>
                  <label className="onboarding-field"><span className="onboarding-label">{copy.penaltyAmount}</span><input className="onboarding-input" type="number" min="0" step={editorRow.penalty_type === "fixed" ? currencyAmountStep(editorRow.currency, currencies) : "0.01"} value={editorRow.penalty_amount} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ penalty_amount: event.target.value }); }} /></label>
                  <label className="onboarding-field"><span className="onboarding-label">{copy.penaltyDescription}</span><input className="onboarding-input" value={editorRow.penalty_description} onChange={(event) => { setEditorFeedback(""); updateEditorRow({ penalty_description: event.target.value }); }} /></label>
                </div>
              </section>

              {editorFeedback ? <div className="onboarding-status onboarding-status--error">{editorFeedback}</div> : null}
            </div>

            <div className="onboarding-entryModal__footer">
              <button type="button" className="onboarding-secondaryBtn" onClick={closeEditor}>{copy.createModalCancel}</button>
              <button type="button" className="onboarding-primaryBtn" onClick={confirmEditor}>
                {editorMode === "edit" ? <FiEdit2 size={16} /> : <FiPlus size={16} />}
                {editorMode === "edit" ? copy.editModalConfirm : copy.createModalConfirm}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
